import {BREAKPOINT_PRIORITY} from '%helpers%/constants';

export const getDiffArray = (a1, a2) => {
  return a1.filter(i => !a2.includes(i)).concat(a2.filter(i => !a1.includes(i)));
};

export const getPropertyValue = (key, el = document.querySelector(':root')) => {
  if (!el) return null;
  return getComputedStyle(el).getPropertyValue(key);
};

export const setPropertyValue = (key, value, el = document.querySelector(':root')) => {
  if (!el) return;
  return el.style.setProperty(key, value);
};

export const getBreakpointPriority = label => {
  return BREAKPOINT_PRIORITY.indexOf(label.toLowerCase());
};

export const getDeviceType = () => {
  const breakpointEntries = [
    [getPropertyValue('--bs-breakpoint-mobile'), 'mobile'],
    [getPropertyValue('--bs-breakpoint-tablet'), 'tablet'],
    [getPropertyValue('--bs-breakpoint-desktop'), 'desktop'],
    [getPropertyValue('--bs-breakpoint-lg-down'), 'lg-down'],
    [getPropertyValue('--bs-breakpoint-lg'), 'lg'],
    [getPropertyValue('--bs-breakpoint-xl-down'), 'xl-down'],
    [getPropertyValue('--bs-breakpoint-xl'), 'xl'],
  ];

  for (const [media, label] of breakpointEntries) {
    if (media && window.matchMedia(media).matches) {
      return {
        type: label,
        priority: BREAKPOINT_PRIORITY.indexOf(label),
      };
    }
  }

  return {type: null, priority: -1};
};

export const isMatchMedia = key => {
  const media = getPropertyValue(`--bs-breakpoint-${key}`);
  return window.matchMedia(media).matches;
};

export const isMatchMediaGrid = key => {
  const media = getPropertyValue(`--bs-grid-breakpoint-${key}`);
  return window.matchMedia(media).matches;
};

export const isMatchMediaByRoot = key => {
  return window.matchMedia(getComputedStyle(document.querySelector(':root')).getPropertyValue(key)).matches;
};

export const isDeviceType = dt => {
  return getDeviceType() === dt;
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const getScrollBarWidth = () => {
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth#usage_notes
  const documentWidth = document.documentElement.clientWidth;
  return Math.abs(window.innerWidth - documentWidth);
};

export const getElementScrollBarWidth = el => {
  if (!el) return 0;
  return Math.abs(el.offsetWidth - el.clientWidth);
};

export const escapeHtml = str => {
  return (str + '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

export const getIframeVideo = (src, newParams = {}) => {
  const getYoutubeIdInUrl = url => {
    let videoId = '';
    // Find videoId in urls such as https://www.youtube.com/watch?v=-WHOer3vKFs
    let watchMatch = url.match(/watch\?v=(.+)(?=\?|$)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    } else {
      // Find videoId in urls such as https://youtu.be/4KShMXTr-nw
      const match = url.match(/[^\/][\w]+(?=\?|$)/);
      videoId = match ? match[0] : '';
    }

    return videoId;
  };

  const getIFrameUrl = () => {
    const urlString = /youtube\.com|youtu\.be/i.test(src) ? getYoutubeEmbedUrl(src) : src;
    if (!Object.keys(newParams).length) return urlString;
    const url = new URL(urlString);
    Object.keys(newParams).forEach(key => {
      url.searchParams.set(key, newParams[key]);
    });
    return url;
  };

  const getYoutubeEmbedUrl = url => {
    let embedUrl;
    if (/\/embed/i.test(url)) {
      embedUrl = url;
    } else {
      embedUrl = `https://www.youtube.com/embed/${getYoutubeIdInUrl(url)}`;
    }

    return embedUrl;
  };

  const iframeUrl = getIFrameUrl();
  const iframe = document.createElement('iframe');
  iframe.classList = 'video';
  iframe.src = iframeUrl;
  iframe.frameborder = 0;
  iframe.allow = 'autoplay; fullscreen';
  iframe.setAttribute('allowfullscreen', '');

  return iframe;
};

//== SCROLL_TO

export const scrollToY = yPos => {
  const scroll = () => {
    window.scrollTo({
      top: yPos,
      behavior: 'smooth',
    });
  };
  window.requestAnimationFrame(scroll);
  scroll();
};

export const scrollTo = (yPos, callback) => {
  const fixedOffset = yPos.toFixed();
  const onScroll = function () {
    if (window.pageYOffset.toFixed() === fixedOffset) {
      window.removeEventListener('scroll', onScroll);
      typeof callback === 'function' && callback();
    }
  };

  window.addEventListener('scroll', onScroll);
  onScroll();
  scrollToY(yPos);
};

//== hoverIntent

export const hoverIntent = (elem, overFn, outFn, delay = 100) => {
  if (!elem) return;
  let timer = null;
  elem.addEventListener('mouseenter', e => {
    timer = setTimeout(() => overFn(), delay);
  });
  elem.addEventListener('mouseleave', e => {
    clearTimeout(timer);
    outFn();
  });
};

//== IntersectionObserver

export const observeElementIntersection = (el, callback, alertSettings = {}) => {
  const observer = new IntersectionObserver(
    function (entries) {
      typeof callback === 'function' && callback(entries[0].isIntersecting);
      // if (entries[0].isIntersecting === true) typeof visibleCallback === "function" && visibleCallback(el);
      // else typeof hiddenCallback === "function" && hiddenCallback(el);
    },
    {threshold: [1], ...alertSettings}
  );

  observer.observe(el);

  return observer;
};

export const unobserveElementIntersection = (observer, el) => {
  observer.unobserve(el);
};

//== Sticky

export function getStickyScrollY(el) {
  if (!el) return 0;

  const rect = el.getBoundingClientRect();
  const scrollTop = window.scrollY || window.pageYOffset;

  const style = getComputedStyle(el);
  const top = parseInt(style.top) || 0;

  return rect.top + scrollTop - top;
}

export function getSafeStickyScrollY(element) {
  const hadSticky = element.classList.contains('is-sticky');
  if (hadSticky) element.classList.remove('is-sticky');

  const scrollY = getStickyScrollY(element);

  if (hadSticky) element.classList.add('is-sticky');
  return scrollY;
}
